import React, { useState, useRef, useEffect, useCallback, memo, useMemo } from 'react';
import { Modal } from 'react-bootstrap';
import { Building, RoofAnchorPoint, RoofAccessPoint, AnchorPointSafetyStatus, BlueprintLocation, PhotoModel } from '../types/protocols';
import { translate } from '../types/EnumTranslations';
import { useFirebaseImage } from '../Helpers/ImageLoader';
import PhotoGallery from './PhotoGallery';
import { ChevronDown, ChevronUp } from 'lucide-react';
import {
    getAnchorPointStatusColor,
    ACCESS_POINT_COLOR,
    getScaledPositionWithContain
} from '../Helpers/RoofSafetyUtils';

interface RoofSafetyAnchorPointsModalProps {
    show: boolean;
    onHide: () => void;
    building: Building;
    buildingKey: string;
    anchorPoints: { [key: string]: RoofAnchorPoint };
    accessPositions?: { [key: string]: RoofAccessPoint };
}

// ── Map Marker Component ──────────────────────────────────────────────

interface PointMarkerProps {
    pointKey: string;
    type: 'anchor' | 'access';
    position: { x: number; y: number };
    isHovered: boolean;
    isExpanded: boolean;
    color: string;
    label: React.ReactNode;
    title: string;
    onSelect: (type: 'anchor' | 'access', key: string) => void;
    onHover: (type: 'anchor' | 'access', key: string) => void;
    onLeave: () => void;
}

const PointMarker = memo<PointMarkerProps>(({
    pointKey,
    type,
    position,
    isHovered,
    isExpanded,
    color,
    label,
    title,
    onSelect,
    onHover,
    onLeave
}) => {
    const size = isExpanded ? 44 : 28;
    const fontSize = isExpanded ? 18 : 12;

    return (
        <div
            onClick={() => onSelect(type, pointKey)}
            onMouseEnter={() => onHover(type, pointKey)}
            onMouseLeave={onLeave}
            style={{
                position: 'absolute',
                left: `${position.x}px`,
                top: `${position.y}px`,
                transform: `translate(-50%, -50%) scale(${isHovered && !isExpanded ? 1.1 : 1})`,
                width: `${size}px`,
                height: `${size}px`,
                borderRadius: '50%',
                backgroundColor: color,
                border: isExpanded ? '4px solid white' : '3px solid white',
                boxShadow: isExpanded ? '0 4px 16px rgba(0,0,0,0.4)' : '0 2px 8px rgba(0,0,0,0.3)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: 'bold',
                fontSize: `${fontSize}px`,
                transition: 'all 0.2s ease',
                zIndex: isExpanded ? 200 : (isHovered ? 100 : 10)
            }}
            title={title}
        >
            {label}
        </div>
    );
});
PointMarker.displayName = 'PointMarker';

// ── Unified Point List Item ───────────────────────────────────────────

interface PointListItemProps {
    pointKey: string;
    type: 'anchor' | 'access';
    point: RoofAnchorPoint | RoofAccessPoint;
    index: number;
    isExpanded: boolean;
    isHovered: boolean;
    buildingKey: string;
    onSelect: (type: 'anchor' | 'access', key: string) => void;
    onHover: (type: 'anchor' | 'access', key: string) => void;
    onLeave: () => void;
}

const PointListItem = memo<PointListItemProps>(({
    pointKey,
    type,
    point,
    index,
    isExpanded,
    isHovered,
    buildingKey,
    onSelect,
    onHover,
    onLeave
}) => {
    const isAnchor = type === 'anchor';
    const anchorPoint = isAnchor ? (point as RoofAnchorPoint) : null;
    const color = isAnchor ? getAnchorPointStatusColor(anchorPoint!.safetyStatus) : ACCESS_POINT_COLOR;
    const label = isAnchor ? `Fästpunkt ${index + 1}` : `Takaccesspunkt ${index + 1}`;
    const icon = isAnchor ? index + 1 : '↑';

    return (
        <div
            style={{
                border: `2px solid ${isHovered || isExpanded ? color : '#e9ecef'}`,
                borderRadius: '8px',
                marginBottom: '10px',
                overflow: 'hidden',
                transition: 'border-color 0.2s ease'
            }}
            onMouseEnter={() => onHover(type, pointKey)}
            onMouseLeave={onLeave}
        >
            <div
                onClick={() => onSelect(type, pointKey)}
                style={{
                    padding: '12px 15px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    backgroundColor: isExpanded ? '#f8f9fa' : 'white'
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div
                        style={{
                            width: '24px',
                            height: '24px',
                            borderRadius: '50%',
                            backgroundColor: color,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: isAnchor ? '11px' : '14px'
                        }}
                    >
                        {icon}
                    </div>
                    <div>
                        <div style={{ fontWeight: 600, fontSize: '14px' }}>
                            {label}
                        </div>
                        {isAnchor && anchorPoint && (
                            <div style={{ fontSize: '13px', color: '#6c757d' }}>
                                {translate(anchorPoint.safetyStatus)}
                            </div>
                        )}
                    </div>
                </div>
                {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </div>

            {isExpanded && (
                <div style={{ padding: '15px', borderTop: '1px solid #e9ecef' }}>
                    {point.comment && (
                        <div style={{ marginBottom: '15px' }}>
                            <div style={{ fontWeight: 500, marginBottom: '5px', fontSize: '13px', color: '#6c757d' }}>
                                Kommentar
                            </div>
                            <div style={{ fontSize: '14px' }}>{point.comment}</div>
                        </div>
                    )}
                    {point.photos && Object.keys(point.photos).length > 0 && (
                        <div>
                            <div style={{ fontWeight: 500, marginBottom: '10px', fontSize: '13px', color: '#6c757d' }}>
                                Foton
                            </div>
                            <PhotoGallery
                                photos={point.photos}
                                buildingKey={buildingKey}
                            />
                        </div>
                    )}
                    {!point.comment && (!point.photos || Object.keys(point.photos).length === 0) && (
                        <div style={{ color: '#6c757d', fontStyle: 'italic', fontSize: '14px' }}>
                            Inga ytterligare detaljer
                        </div>
                    )}
                </div>
            )}
        </div>
    );
});
PointListItem.displayName = 'PointListItem';

// ── Main Modal Component ──────────────────────────────────────────────

const RoofSafetyAnchorPointsModal: React.FC<RoofSafetyAnchorPointsModalProps> = ({
    show,
    onHide,
    building,
    buildingKey,
    anchorPoints,
    accessPositions
}) => {
    const [selectedPoint, setSelectedPoint] = useState<{ type: 'anchor' | 'access'; key: string } | null>(null);
    const [hoveredPoint, setHoveredPoint] = useState<{ type: 'anchor' | 'access'; key: string } | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerDimensions, setContainerDimensions] = useState({ width: 0, height: 0 });

    const currentMapImageKey = building.model?.currentMapImageKey;
    const mapImageRaw = currentMapImageKey && building.model?.mapImages?.[currentMapImageKey];
    const mapImage = mapImageRaw && typeof mapImageRaw !== 'string' ? mapImageRaw : null;
    const { src: mapSrc, isLoading, hasError } = useFirebaseImage(mapImage?.storagePath);

    useEffect(() => {
        const updateDimensions = () => {
            if (containerRef.current) {
                setContainerDimensions({
                    width: containerRef.current.offsetWidth,
                    height: containerRef.current.offsetHeight
                });
            }
        };

        const resizeObserver = new ResizeObserver(updateDimensions);
        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }

        return () => resizeObserver.disconnect();
    }, [show]);

    const getScaledPosition = useCallback((position: BlueprintLocation) => {
        return getScaledPositionWithContain(position, containerDimensions, mapImage);
    }, [containerDimensions, mapImage]);

    const handleSelect = useCallback((type: 'anchor' | 'access', key: string) => {
        setSelectedPoint(prev =>
            prev?.type === type && prev.key === key ? null : { type, key }
        );
    }, []);

    const handleHover = useCallback((type: 'anchor' | 'access', key: string) => {
        setHoveredPoint({ type, key });
    }, []);

    const handleLeave = useCallback(() => {
        setHoveredPoint(null);
    }, []);

    const anchorPointsList = useMemo(() => Object.entries(anchorPoints), [anchorPoints]);
    const accessPositionsList = useMemo(
        () => accessPositions ? Object.entries(accessPositions) : [],
        [accessPositions]
    );

    const statusCounts = useMemo(() => {
        let safe = 0, notSafe = 0, undetermined = 0;
        for (const [, ap] of anchorPointsList) {
            switch (ap.safetyStatus) {
                case AnchorPointSafetyStatus.SAFE: safe++; break;
                case AnchorPointSafetyStatus.NOT_SAFE: notSafe++; break;
                default: undetermined++; break;
            }
        }
        return { safe, notSafe, undetermined };
    }, [anchorPointsList]);

    const { safe: safeCount, notSafe: notSafeCount, undetermined: undeterminedCount } = statusCounts;

    return (
        <Modal show={show} onHide={onHide} size="xl" centered>
            <Modal.Header closeButton>
                <Modal.Title>Taksäkerhet - Översikt</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ padding: '20px' }}>
                {/* Legend */}
                <div style={{
                    display: 'flex',
                    gap: '24px',
                    marginBottom: '20px',
                    flexWrap: 'wrap',
                    padding: '12px 16px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px'
                }}>
                    <div style={{ fontWeight: 500, marginRight: '8px' }}>Fästpunkter:</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <div style={{
                            width: '12px',
                            height: '12px',
                            borderRadius: '50%',
                            backgroundColor: getAnchorPointStatusColor(AnchorPointSafetyStatus.SAFE)
                        }} />
                        <span>{safeCount} {translate(AnchorPointSafetyStatus.SAFE).toLowerCase()}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <div style={{
                            width: '12px',
                            height: '12px',
                            borderRadius: '50%',
                            backgroundColor: getAnchorPointStatusColor(AnchorPointSafetyStatus.NOT_SAFE)
                        }} />
                        <span>{notSafeCount} {translate(AnchorPointSafetyStatus.NOT_SAFE).toLowerCase()}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <div style={{
                            width: '12px',
                            height: '12px',
                            borderRadius: '50%',
                            backgroundColor: getAnchorPointStatusColor(AnchorPointSafetyStatus.UNDETERMINED)
                        }} />
                        <span>{undeterminedCount} {translate(AnchorPointSafetyStatus.UNDETERMINED).toLowerCase()}</span>
                    </div>
                    {accessPositionsList.length > 0 && (
                        <>
                            <div style={{ borderLeft: '1px solid #dee2e6', height: '20px' }} />
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <div style={{
                                    width: '12px',
                                    height: '12px',
                                    borderRadius: '50%',
                                    backgroundColor: ACCESS_POINT_COLOR
                                }} />
                                <span>{accessPositionsList.length} takaccesspunkter</span>
                            </div>
                        </>
                    )}
                </div>

                <div style={{
                    display: 'grid',
                    gridTemplateColumns: mapImage ? '1fr 350px' : '1fr',
                    gap: '20px',
                    height: 'calc(70vh - 150px)',
                    minHeight: '400px'
                }}>
                    {/* Map with markers */}
                    {mapImage && (
                        <div
                            ref={containerRef}
                            style={{
                                position: 'relative',
                                borderRadius: '8px',
                                overflow: 'hidden',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                backgroundColor: '#f8f9fa',
                                height: '100%'
                            }}
                        >
                            {isLoading ? (
                                <div style={{
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    Laddar karta...
                                </div>
                            ) : hasError || !mapSrc ? (
                                <div style={{
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#6c757d'
                                }}>
                                    Kunde inte ladda kartan
                                </div>
                            ) : (
                                <>
                                    <img
                                        src={mapSrc}
                                        alt="Byggnadskarta"
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'contain',
                                            display: 'block'
                                        }}
                                        onLoad={() => {
                                            if (containerRef.current) {
                                                setContainerDimensions({
                                                    width: containerRef.current.offsetWidth,
                                                    height: containerRef.current.offsetHeight
                                                });
                                            }
                                        }}
                                    />
                                    {anchorPointsList.map(([key, ap], index) => {
                                        const pos = ap.position ? getScaledPosition(ap.position) : null;
                                        if (!pos) return null;
                                        return (
                                            <PointMarker
                                                key={key}
                                                pointKey={key}
                                                type="anchor"
                                                position={pos}
                                                isHovered={hoveredPoint?.type === 'anchor' && hoveredPoint.key === key}
                                                isExpanded={selectedPoint?.type === 'anchor' && selectedPoint.key === key}
                                                color={getAnchorPointStatusColor(ap.safetyStatus)}
                                                label={index + 1}
                                                title={`Fästpunkt ${index + 1}: ${translate(ap.safetyStatus)}`}
                                                onSelect={handleSelect}
                                                onHover={handleHover}
                                                onLeave={handleLeave}
                                            />
                                        );
                                    })}
                                    {accessPositionsList.map(([key, accessPoint], index) => {
                                        const pos = getScaledPosition(accessPoint.position);
                                        if (!pos) return null;
                                        return (
                                            <PointMarker
                                                key={`access-${key}`}
                                                pointKey={key}
                                                type="access"
                                                position={pos}
                                                isHovered={hoveredPoint?.type === 'access' && hoveredPoint.key === key}
                                                isExpanded={selectedPoint?.type === 'access' && selectedPoint.key === key}
                                                color={ACCESS_POINT_COLOR}
                                                label="↑"
                                                title={`Takaccesspunkt ${index + 1}`}
                                                onSelect={handleSelect}
                                                onHover={handleHover}
                                                onLeave={handleLeave}
                                            />
                                        );
                                    })}
                                </>
                            )}
                        </div>
                    )}

                    {/* Points list */}
                    <div style={{
                        height: '100%',
                        overflowY: 'auto',
                        paddingRight: '5px'
                    }}>
                        {anchorPointsList.length > 0 && (
                            <>
                                <div style={{ fontWeight: 600, fontSize: '13px', color: '#495057', marginBottom: '10px' }}>
                                    Fästpunkter
                                </div>
                                {anchorPointsList.map(([key, ap], index) => (
                                    <PointListItem
                                        key={key}
                                        pointKey={key}
                                        type="anchor"
                                        point={ap}
                                        index={index}
                                        isExpanded={selectedPoint?.type === 'anchor' && selectedPoint.key === key}
                                        isHovered={hoveredPoint?.type === 'anchor' && hoveredPoint.key === key}
                                        buildingKey={buildingKey}
                                        onSelect={handleSelect}
                                        onHover={handleHover}
                                        onLeave={handleLeave}
                                    />
                                ))}
                            </>
                        )}

                        {accessPositionsList.length > 0 && (
                            <>
                                <div style={{
                                    fontWeight: 600,
                                    fontSize: '13px',
                                    color: '#495057',
                                    marginBottom: '10px',
                                    marginTop: anchorPointsList.length > 0 ? '20px' : '0'
                                }}>
                                    Takaccesspunkter
                                </div>
                                {accessPositionsList.map(([key, accessPoint], index) => (
                                    <PointListItem
                                        key={key}
                                        pointKey={key}
                                        type="access"
                                        point={accessPoint}
                                        index={index}
                                        isExpanded={selectedPoint?.type === 'access' && selectedPoint.key === key}
                                        isHovered={hoveredPoint?.type === 'access' && hoveredPoint.key === key}
                                        buildingKey={buildingKey}
                                        onSelect={handleSelect}
                                        onHover={handleHover}
                                        onLeave={handleLeave}
                                    />
                                ))}
                            </>
                        )}

                        {anchorPointsList.length === 0 && accessPositionsList.length === 0 && (
                            <div style={{
                                color: '#6c757d',
                                fontStyle: 'italic',
                                textAlign: 'center',
                                paddingTop: '20px'
                            }}>
                                Inga punkter registrerade
                            </div>
                        )}
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default RoofSafetyAnchorPointsModal;
